import React, { useContext, useEffect, useState } from 'react';
import styles from './NewsPage.module.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import NewsCard, { NewsData } from '../../components/NewsCard/NewsCard';
import TabButton from '../../components/UI/TabButton/TabButton';
import { Helmet } from 'react-helmet';
import Dropdown from '../../components/UI/Dropdown/Dropdown';
import NotFoundBlock from '../../components/NotFoundBlock/NotFoundBlock';
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';
import api from '../../utils/api';
import { transformNewsArticles } from '../../utils/transformers';

type Option = {
  value: string;
  label: string;
  description?: string;
};

export default function NewsPage() {
  const [newsData, setNewsData] = useState<NewsData[]>([]);
  const [newsTypes, setNewsTypes] = useState<string[]>(['Общие']);
  const [projects, setProjects] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string | null>('Общие');
  const [selectedProject, setSelectedProject] = useState<Option | null>({ value: 'all', label: 'Все проекты' });
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);
  const [isError, setIsError] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>('');

  const parseNewsDate = (value: string): number => {
    if (!value) return 0;
    const cleaned = value
      .toString()
      .replace(/\bг\.?|\bгода?\b/gi, '')
      .trim();

    const dmy = cleaned.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dmy) {
      const [, d, m, y] = dmy;
      const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      return date.getTime();
    }

    const months: Record<string, number> = {
      'января': 0,
      'февраля': 1,
      'марта': 2,
      'апреля': 3,
      'мая': 4,
      'июня': 5,
      'июля': 6,
      'августа': 7,
      'сентября': 8,
      'октября': 9,
      'ноября': 10,
      'декабря': 11,
    };
    const ru = cleaned.toLowerCase().match(/^(\d{1,2})\s+([а-яё]+)\s+(\d{4})$/i);
    if (ru) {
      const [, d, mon, y] = ru as unknown as [string, string, string, string];
      const monthIndex = months[mon];
      if (monthIndex !== undefined) {
        const date = new Date(parseInt(y, 10), monthIndex, parseInt(d, 10));
        return date.getTime();
      }
    }

    const t = Date.parse(cleaned);
    return isNaN(t) ? 0 : t;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.news.getAll();
        
        const transformedData = transformNewsArticles(response);
        const sortedData = [...transformedData].sort((a, b) => parseNewsDate(b.date) - parseNewsDate(a.date));
        
        const uniqueTopics = Array.from(new Set(sortedData.map(news => news.topic)))
          .filter(topic => topic !== 'Общие');
        const allNewsTypes = ['Общие', ...uniqueTopics];
        
        const uniqueProjects = Array.from(new Set(sortedData.map(news => news.project)))
          .filter(project => project && project.trim() !== '');
        
        setNewsData(sortedData);
        setNewsTypes(allNewsTypes);
        setProjects(uniqueProjects);
        
        if (selectedProject && selectedProject.value !== 'all' && !uniqueProjects.includes(selectedProject.value)) {
          setSelectedProject({ value: 'all', label: 'Все проекты' });
        }
        
        setIsError(false);
      } catch (error) {
        console.error('Error fetching news data:', error);

        setNewsData([]);
        setIsError(true);
      } finally {
        setIsInitialLoad(false);
      }
    }
    fetchData();
  }, [setIsInitialLoad, selectedProject]);

  const projectOptions: Option[] = [
    { value: 'all', label: 'Все проекты' },
    ...projects.map((project) => ({
      value: project,
      label: project,
    }))
  ];

  const handleTypeClick = (type: string) => {
    setSelectedType((prevType) => (prevType === type ? null : type));
  };

  const handleProjectChange = (selectedOption: Option | null) => {
    setSelectedProject(selectedOption);
  };

  const filteredNews = newsData.filter((news) => {
    const matchesType = selectedType === 'Общие' || news.topic === selectedType;
    const matchesProject = selectedProject?.value === 'all' || news.project === selectedProject?.value;
    return matchesType && matchesProject;
  });

  return (
    <>
      <Helmet>
        <title>Новости | Восток Строй</title>
        <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" />
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vostokstroy.com/news" />

        <meta property="og:title" content="Ведущему девелоперу Каспийска — 10 лет" />
        <meta property="og:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/news" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ведущему девелоперу Каспийска — 10 лет" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />

        <link rel="icon" type="image/svg+xml" href="/favicons/main-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/main-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/main-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/main-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/main-fav-180x180.png" />
      </Helmet>
      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
      <div className={styles.news_page_wrapper}>
        <Header theme='light' />
        <div className={styles.wrapper}>
          <div className={styles.wrapper_head}>
            <h2>Новости</h2>
            <div className={styles.filers_wrapper}>
              <Dropdown
                options={projectOptions}
                selected={selectedProject}
                onSelect={handleProjectChange}
                placeholder="Проект"
              />
              <div className={styles.filters}>
                {newsTypes.map((tab, index) => (
                  <TabButton
                    key={index}
                    isActive={selectedType === tab}
                    onClick={() => handleTypeClick(tab)}
                    theme='light'
                  >
                    {tab}
                  </TabButton>
                ))}
              </div>
            </div>
          </div>
            {filteredNews.length === 0 && !isError ? (
              <NotFoundBlock type="news" />
            ) : isError ? (
              <div className={styles.error_message}>
                <p>Произошла ошибка при загрузке новостей. Пожалуйста, попробуйте позже.</p>
                {debugInfo && (
                  <details style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                    <summary>Техническая информация для разработчика</summary>
                    <p style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                      {debugInfo}
                    </p>
                    <p style={{ fontSize: '12px', marginTop: '5px' }}>
                      Откройте консоль разработчика (F12) для получения подробной диагностики.
                    </p>
                  </details>
                )}
              </div>
            ) : (
              <div className={styles.news_wrapper}>
                {filteredNews.map((data) => (
                  <NewsCard
                    key={data.id}
                    id={data.id}
                    project={data.project}
                    topic={data.topic}
                    date={data.date}
                    title={data.title}
                    shortDescription={data.shortDescription}
                    content={data.content}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <Footer theme='light' />
        </>
      )}
    </>
  );
}